import { Buffer } from 'buffer';

/**
 * Announced when the backend refuses the stored session (401), with the
 * refused token as `detail.token`, so the session owner can sign in again.
 */
export const SESSION_EXPIRED_EVENT = 'session-expired';

/** Announced when a different session token is stored, so the notification stream reconnects with it. */
export const TOKEN_EVENT = 'token';

/** How long before its expiry a session counts as over: no request should start on one about to lapse. */
const EXPIRY_MARGIN_MS = 60_000;

/** The token's claims, or null if it can't be read. */
const claims = (token: string): any => {
  try {
    return JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString('utf8'));
  } catch {
    return null;
  }
};

/** The expiry of a session token, in Unix seconds: `exp`, or `expires_at` on tokens from before it. */
const tokenExpiry = (payload: any): number =>
  typeof payload?.exp === 'number' ? payload.exp : Number(payload?.expires_at);

/** Whether the token has run out, or will within a minute. An unreadable token counts as run out. */
export const isLapsed = (token: string): boolean => {
  const expiresAt = tokenExpiry(claims(token));
  return !Number.isFinite(expiresAt) || expiresAt * 1000 - Date.now() < EXPIRY_MARGIN_MS;
};

/**
 * The stored session token, if it is for this account and network and has
 * more than a minute left. Reusing it spares the person a signing prompt on
 * every page load.
 */
export const currentSession = (accountId: string, network: string): string | null => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  const payload = claims(token);
  if (payload?.accountId !== accountId || payload?.network !== network) return null;
  return isLapsed(token) ? null : token;
};
